// src/pages/subjects/student/SubjectStudentShell.jsx

import React, { useState, useEffect, useMemo, useRef } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { gql, useMutation, useQuery } from "@apollo/client";
import "../../../styles/Subjects.css";
import "../../../styles/SubjectRightVar1.css";
import learnHutLogo from "../../../Logos To be used/learnhut_logo_white_green.png";
import collapsableSettings from "../../../Logos To be used/Collapsable settings.svg";
import subjectLeftHandler from "../../../Logos To be used/subject left handler.svg";
import SubjectExp1Right from "./SubjectStudentContent";
import {
  TECHNISCHE_MECHANIK_1_SUBJECT_ID,
  getAltKlausurenPracticeLabel,
  getDirectAltklausurenPracticeLabel,
  isDirectAltklausurenPracticeSubject,
  isKlausurPraxisSubject,
} from "../exceptions/subjectExceptions";

// 1) Find subject by slug
const SUBJECTS_QUERY = gql`
  query Subjects {
    subjects {
      id
      title
      slug
      description
      isPublic
    }
  }
`;

// 2) Fetch lesson categories + lessons for this subject (PUBLIC for students)
const LESSON_CATEGORIES_BY_SUBJECT_QUERY = gql`
  query LessonCategoriesBySubject($subjectId: ID!, $mode: LessonVisibilityMode!) {
    lessonCategoriesBySubject(subjectId: $subjectId, mode: $mode) {
      id
      title
      position
      lessons(mode: $mode) {
        id
        title
        slug
        order
        isPublic
      }
    }
  }
`;

// 3) Fetch persisted completion for this student
const LIST_PROGRESS_QUERY = gql`
  query ListProgressByStudent($input: ListProgressByStudentInput!) {
    listProgressByStudent(input: $input) {
      lessonId
      completed
    }
  }
`;

const UPDATE_LAST_ACCESSED_MUTATION = gql`
  mutation UpdateLastAccessed($input: UpdateLastAccessedInput!) {
    updateLastAccessed(input: $input)
  }
`;

const sortNum = (a, b, key) => {
  const av = typeof a?.[key] === "number" ? a[key] : 0;
  const bv = typeof b?.[key] === "number" ? b[key] : 0;
  return av - bv;
};

const SubjectExp1 = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { subjectSlug, lessonSlug } = useParams();

  const [isFirstDropdownOpen, setFirstDropdownOpen] = useState(false);
  const [isPraxisDropdownOpen, setPraxisDropdownOpen] = useState(false);
  const [isCollapsed, setCollapsed] = useState(false);

  const [selectedLessonId, setSelectedLessonId] = useState(null);
  const [selectedLessonTitle, setSelectedLessonTitle] = useState("");
  const [showAltKlausurenComing, setShowAltKlausurenComing] = useState(false);
  const [showKlausurPraxis, setShowKlausurPraxis] = useState(false);
  const [completedLessons, setCompletedLessons] = useState(new Set());

  const userId = localStorage.getItem("userID");
  const lastAccessedSaveKeyRef = useRef("");
  const [updateLastAccessed] = useMutation(UPDATE_LAST_ACCESSED_MUTATION);

  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    document.documentElement.classList.add("subject-shell-open");
    document.body.classList.add("subject-shell-open");

    return () => {
      document.documentElement.classList.remove("subject-shell-open");
      document.body.classList.remove("subject-shell-open");
    };
  }, []);

  // ✅ Go back to the correct dashboard based on current toggle mode
  const goToDashboard = () => {
    const role = localStorage.getItem("userRole");
    const isTutor = role === "TUTOR" || role === "ADMIN";

    // if not tutor, always student dashboard
    if (!isTutor) {
      navigate("/dashboard");
      return;
    }

    // if tutor, respect current view mode
    const mode = localStorage.getItem("tutorViewMode");
    if (mode === "tutor") navigate("/dashboard-tutor");
    else navigate("/dashboard");
  };

  // ----- Load subject (by slug) -----
  const { data: subjectsData, loading: subjectsLoading, error: subjectsError } =
    useQuery(SUBJECTS_QUERY, { fetchPolicy: "network-only" });

  const subject = useMemo(() => {
    const list = subjectsData?.subjects || [];
    return list.find((s) => s?.slug === subjectSlug) || null;
  }, [subjectsData, subjectSlug]);

  const subjectId = subject?.id || null;
  const altKlausurenPracticeLabel = getAltKlausurenPracticeLabel(subjectId);
  const hasKlausurPraxis = isKlausurPraxisSubject(subjectId);
  const hasDirectAltklausurenPractice = isDirectAltklausurenPracticeSubject(subjectId);
  const directAltklausurenPracticeLabel = getDirectAltklausurenPracticeLabel(subjectId);
  const role = localStorage.getItem("userRole");
  const isTutorOrAdmin = role === "TUTOR" || role === "ADMIN";
  const showServerContentNotice =
    Boolean(selectedLessonId) &&
    !isTutorOrAdmin &&
    String(subjectId || "").toLowerCase() === TECHNISCHE_MECHANIK_1_SUBJECT_ID;

  useEffect(() => {
    if (!userId || !subjectId || !selectedLessonId) return;

    const role = localStorage.getItem("userRole");
    if (role === "TUTOR" || role === "ADMIN") return;

    const saveKey = `${userId}:${subjectId}:${selectedLessonId}`;
    if (lastAccessedSaveKeyRef.current === saveKey) return;
    lastAccessedSaveKeyRef.current = saveKey;

    updateLastAccessed({
      variables: {
        input: {
          userId,
          lessonId: selectedLessonId,
          lastAccessedAt: new Date().toISOString(),
        },
      },
    }).catch(() => {
      lastAccessedSaveKeyRef.current = "";
    });
  }, [selectedLessonId, subjectId, updateLastAccessed, userId]);

  // ----- Load lesson categories -----
  const {
    data: lessonCatData,
    loading: lessonCatLoading,
    error: lessonCatError,
  } = useQuery(LESSON_CATEGORIES_BY_SUBJECT_QUERY, {
    variables: subjectId ? { subjectId, mode: "PUBLIC" } : undefined,
    skip: !subjectId,
    fetchPolicy: "network-only",
  });

  const lessonCategories = useMemo(() => {
    const cats = lessonCatData?.lessonCategoriesBySubject || [];
    const sortedCats = [...cats].sort((a, b) => sortNum(a, b, "position"));

    return sortedCats.map((c) => {
      const lessons = (c.lessons || [])
        .slice()
        .sort((a, b) => sortNum(a, b, "order"));
      return { ...c, lessons };
    });
  }, [lessonCatData]);

  // Flatten all lessons
  const sortedLessons = useMemo(() => {
    const all = [];
    lessonCategories.forEach((cat) => {
      (cat.lessons || []).forEach((l) => all.push(l));
    });
    return all;
  }, [lessonCategories]);

  // ----- Load progress -----
  const { data: progData } = useQuery(LIST_PROGRESS_QUERY, {
    variables: { input: { userId } },
    skip: !userId,
    fetchPolicy: "network-only",
  });

  useEffect(() => {
    if (progData?.listProgressByStudent) {
      const done = new Set(
        progData.listProgressByStudent
          .filter((p) => p.completed)
          .map((p) => p.lessonId)
      );
      setCompletedLessons(done);
    }
  }, [progData]);

  // ----- Select lesson from URL slug (backend slug) -----
  useEffect(() => {
    if (!lessonSlug || sortedLessons.length === 0) return;

    const match = sortedLessons.find((l) => l?.slug === lessonSlug);
    if (match) {
      setSelectedLessonId(match.id);
      setSelectedLessonTitle(match.title);
      setShowAltKlausurenComing(false);
      setShowKlausurPraxis(false);
    } else {
      // If slug is invalid, go back to subject home view
      setSelectedLessonId(null);
      setSelectedLessonTitle("");
      setShowAltKlausurenComing(false);
      setShowKlausurPraxis(false);
      navigate(`/subject/${subjectSlug}/learn`, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lessonSlug, sortedLessons, subjectSlug]);

  const handleLessonComplete = (lessonId) => {
    setCompletedLessons((prev) => new Set(prev).add(lessonId));
  };

  const handleLessonSelect = (id, title, slugFromBackend) => {
    setSelectedLessonId(id);
    setSelectedLessonTitle(title);
    setShowAltKlausurenComing(false);
    setShowKlausurPraxis(false);

    // ✅ Use backend slug directly (no frontend slug generation)
    navigate(`/subject/${subjectSlug}/learn/${slugFromBackend}`);
  };

  useEffect(() => {
    if (lessonSlug) return;

    const view = new URLSearchParams(location.search).get("view");

    if (view === "altklausuren" || view === "klausuren") {
      setSelectedLessonId(null);
      setSelectedLessonTitle(altKlausurenPracticeLabel);
      setShowAltKlausurenComing(true);
      setShowKlausurPraxis(false);
      return;
    }

    if (view === "klausur-praxis" && hasKlausurPraxis) {
      setSelectedLessonId(null);
      setSelectedLessonTitle("Klausur Praxis");
      setShowAltKlausurenComing(false);
      setShowKlausurPraxis(true);
      return;
    }

    if (!view) {
      setSelectedLessonId(null);
      setSelectedLessonTitle("");
      setShowAltKlausurenComing(false);
      setShowKlausurPraxis(false);
    }
  }, [
    altKlausurenPracticeLabel,
    hasKlausurPraxis,
    lessonSlug,
    location.search,
  ]);

  const goToAltKlausuren = () => {
    setSelectedLessonId(null);
    setSelectedLessonTitle(altKlausurenPracticeLabel);
    setShowAltKlausurenComing(true);
    setShowKlausurPraxis(false);
    navigate(`/subject/${subjectSlug}/learn?view=altklausuren`);
  };

  const goToKlausurPraxis = () => {
    setSelectedLessonId(null);
    setSelectedLessonTitle("Klausur Praxis");
    setShowAltKlausurenComing(false);
    setShowKlausurPraxis(true);
    navigate(`/subject/${subjectSlug}/learn?view=klausur-praxis`);
  };

  const renderLessonGroup = (title, lessonsArr) => {
    if (!lessonsArr || lessonsArr.length === 0) return null;

    return (
      <div style={{ position: "relative", marginTop: "30px" }}>
        <div className="big-title">{title}</div>
        <div className="dashed-line2" style={{ top: "35px" }} />
        {lessonsArr.map((lesson, idx) => (
          <div
            key={lesson.id}
            style={{
              display: "flex",
              alignItems: "center",
              marginTop: idx === 0 ? "20px" : "15px",
              position: "relative",
            }}
          >
            <div
              className={`progress-point${
                completedLessons.has(lesson.id) ? " completed" : ""
              }`}
            />
            <Link
              className={`lesson-title ${
                selectedLessonId === lesson.id ? "selected-lesson" : ""
              }`}
              to={`/subject/${subjectSlug}/learn/${lesson.slug}`}
            >
              {lesson.title}
            </Link>
          </div>
        ))}
      </div>
    );
  };

  if (subjectsLoading) {
    return <div style={{ padding: 20 }}>Loading...</div>;
  }
  if (subjectsError) {
    return <div style={{ padding: 20 }}>Error loading subject.</div>;
  }
  if (!subject) {
    return <div style={{ padding: 20 }}>Subject not found.</div>;
  }

  return (
    <div className={`subject-page ${isCollapsed ? "collapsed" : ""}`}>
      {/* LEFT */}
      <div className={`left-section ${isCollapsed ? "collapsed" : ""}`}>
        {/* Top */}
        <div className="top-section" onClick={goToDashboard}>
          <img
            src={learnHutLogo}
            alt="LearnHut Logo"
            className="tempo-logo"
            style={{ cursor: "pointer" }}
          />
          {!isCollapsed && (
            <>
              <div className="vertical-line" />
              <span className="academy-name-wrap" style={{ cursor: "pointer" }}>
                <span className="academy-beta">BETA</span>
              </span>
            </>
          )}
        </div>
        {!isCollapsed && <hr className="separator-line" />}

        {/* Middle */}
        {!isCollapsed && (
          <div className="middle-section">
            <h2
              className="subject-title2"
            >
              <Link
                className="subject-title-link2"
                to={`/subject/${subjectSlug}/learn`}
                onClick={() => {
                  setSelectedLessonId(null);
                  setSelectedLessonTitle("");
                  setShowAltKlausurenComing(false);
                  setShowKlausurPraxis(false);
                }}
              >
                {subject.title}
              </Link>
            </h2>

            {hasDirectAltklausurenPractice ? (
              <button
                type="button"
                className={`dropdown-section dropdown-section-button direct-practice-section ${
                  showAltKlausurenComing ? "active" : ""
                }`}
                onClick={goToAltKlausuren}
              >
                <span className="dropdown-direct-mark" aria-hidden="true" />
                <span className="dropdown-text">{directAltklausurenPracticeLabel}</span>
              </button>
            ) : null}

            {/* First dropdown: Kurs & Übungen */}
            <div
              className="dropdown-section"
              onClick={() => setFirstDropdownOpen((o) => !o)}
            >
              <img
                src={subjectLeftHandler}
                alt="Toggle"
                className={`dropdown-icon ${
                  isFirstDropdownOpen ? "rotated" : ""
                }`}
              />
              <span className="dropdown-text">Kurs & Übungen</span>
            </div>

            {isFirstDropdownOpen && (
              <div className="dropdown-content">
                {lessonCatLoading ? (
                  <div style={{ color: "white", opacity: 0.8 }}>Loading…</div>
                ) : lessonCatError ? (
                  <div style={{ color: "white", opacity: 0.8 }}>
                    Fehler beim Laden.
                  </div>
                ) : (
                  lessonCategories.map((cat) =>
                    renderLessonGroup(cat.title, cat.lessons)
                  )
                )}
              </div>
            )}

            {!hasDirectAltklausurenPractice ? (
              <>
                <div
                  className="dropdown-section"
                  onClick={() => setPraxisDropdownOpen((o) => !o)}
                >
                  <img
                    src={subjectLeftHandler}
                    alt="Toggle"
                    className={`dropdown-icon ${isPraxisDropdownOpen ? "rotated" : ""}`}
                  />
                  <span className="dropdown-text">Praxis-Modus</span>
                </div>

                {isPraxisDropdownOpen && (
                  <div className="dropdown-subcontent">
                    <button
                      type="button"
                      className="dropdown-subitem"
                      onClick={goToAltKlausuren}
                    >
                      <span className="dropdown-subbullet" aria-hidden="true">
                        •
                      </span>
                      <span>{altKlausurenPracticeLabel}</span>
                    </button>

                    {hasKlausurPraxis ? (
                      <button
                        type="button"
                        className={`dropdown-subitem ${showKlausurPraxis ? "active" : ""}`}
                        onClick={goToKlausurPraxis}
                      >
                        <span className="dropdown-subbullet" aria-hidden="true">
                          •
                        </span>
                        <span>Klausur Praxis</span>
                      </button>
                    ) : (
                      <div
                        className="dropdown-subitem locked with-tooltip"
                        aria-disabled="true"
                        tabIndex={0}
                      >
                        <span className="dropdown-subbullet" aria-hidden="true">
                          •
                        </span>
                        <span className="dropdown-sublabel">Übungen Generator AI</span>
                        <span className="dropdown-subtooltip">Kommt nächsten Monat</span>
                      </div>
                    )}
                  </div>
                )}
              </>
            ) : null}
          </div>
        )}

        {/* Bottom */}
        <div className="bottom-section">
          <hr className="separator-line bottom-separator" />
          <img
            src={collapsableSettings}
            alt="Collapse"
            className={`collapse-icon ${isCollapsed ? "rotated" : ""}`}
            onClick={() => setCollapsed((c) => !c)}
          />
        </div>
      </div>

      {/* RIGHT */}
      <div className="right-section">
        {showServerContentNotice ? (
          <div className="student-server-status-bar" role="status">
            <div className="student-server-status-inner">
              <div className="student-server-status-badge">
                <span className="student-server-status-dot" aria-hidden="true" />
                <span>Inhalte werden überarbeitet</span>
              </div>
              <div className="student-server-status-copy">
                <p>
                  Für dieses Fach erstellt unser Server gerade neue Inhalte.
                </p>
                <p>
                  Die aktualisierten Inhalte werden in den nächsten Tagen
                  automatisch ergänzt.
                </p>
              </div>
            </div>
          </div>
        ) : null}
        <SubjectExp1Right
          subjectId={subjectId}
          lessonId={selectedLessonId}
          lessonTitle={selectedLessonTitle}
          showAltKlausurenComing={showAltKlausurenComing}
          showKlausurPraxis={showKlausurPraxis}
          lessons={sortedLessons}
          lessonCategories={lessonCategories}
          onLessonSelect={(id, title) => {
            const l = sortedLessons.find((x) => x.id === id);
            if (!l) return;
            handleLessonSelect(id, title, l.slug);
          }}
          onLessonComplete={handleLessonComplete}
        />
      </div>
    </div>
  );
};

export default SubjectExp1;
